import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";

// Enum options extracted from Ruby code
export const categoryOptions = [
  { value: "investment_bank", label: "Investment Bank" },
  { value: "asset_manager", label: "Asset Manager" },
  { value: "private_bank", label: "Private Bank" },
  { value: "family_office", label: "Family Office" },
  { value: "pension_fund", label: "Pension Fund" },
  { value: "sovereign_wealth_fund", label: "Sovereign Wealth Fund" },
  { value: "hedge_fund", label: "Hedge Fund" },
  { value: "academic", label: "Academic" },
  { value: "visitor", label: "Visitor" },
  { value: "insurance_company", label: "Insurance Company" },
  { value: "central_bank", label: "Central Bank" },
  { value: "retail_bank", label: "Retail Bank" },
  { value: "fund_administration", label: "Fund Administration" },
  { value: "corporate", label: "Corporate" },
  { value: "research_house", label: "Research House" },
  { value: "cgp", label: "CGP" },
  { value: "consulting_firm", label: "Consulting Firm" },
];

export const filterCompaniesSchema = z.object({
  key_contains: z.string().nullable().optional(),
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  city_contains: z.string().nullable().optional(),
  country_contains: z.string().nullable().optional(),
  salesforce_id_contains: z.string().nullable().optional(),
  category_contains: z.string().nullable().optional(),
  is_client: z.boolean().optional(),
  is_provider: z.boolean().optional(),
  is_etf_provider: z.boolean().optional(),
  is_fund_provider: z.boolean().optional(),
  is_index_provider: z.boolean().optional(),
  parent_company_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  updated_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  updated_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterCompaniesSchema = z.infer<typeof filterCompaniesSchema>;

export const fields: Field[] = [
  {
    name: "key_contains",
    label: "Key",
    type: "input",
  },
  {
    name: "name_contains",
    label: "Name",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/companies",
    column_name: "name",
  },
  {
    name: "city_contains",
    label: "City",
    type: "input",
  },
  {
    name: "country_contains",
    label: "Country",
    type: "input",
  },
  {
    name: "salesforce_id_contains",
    label: "Salesforce ID",
    type: "input",
  },
  {
    name: "category_contains",
    label: "Category",
    type: "select",
    useTranslation: false,
    options: categoryOptions,
  },
  {
    name: "is_client",
    label: "Is Client",
    type: "checkbox",
  },
  {
    name: "is_provider",
    label: "Is Provider",
    type: "checkbox",
  },
  {
    name: "is_etf_provider",
    label: "Is ETF Provider",
    type: "checkbox",
  },
  {
    name: "is_fund_provider",
    label: "Is Fund Provider",
    type: "checkbox",
  },
  {
    name: "is_index_provider",
    label: "Is Index Provider",
    type: "checkbox",
  },
  {
    name: "parent_company_contains",
    label: "Parent Company",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/companies",
    column_name: "name",
  },
  // Date filters at the end
  {
    name: "created_at_from",
    label: "Created From",
    type: "date",
  },
  {
    name: "created_at_to",
    label: "Created To",
    type: "date",
  },
  {
    name: "updated_at_from",
    label: "Updated From",
    type: "date",
  },
  {
    name: "updated_at_to",
    label: "Updated To",
    type: "date",
  },
];

export const client = "qtl - referential";
export const tableName = "companies";

export function FilterCompanies() {
  return (
    <FilterTable
      client={client}
      schema={filterCompaniesSchema}
      tableName={tableName}
      customBuildPath={() => `${client}/${tableName}`}
      fields={fields}
      columns={2}
    />
  );
}
